/**
 * Funções para exportação de fluxos
 * Sanitiza dados sensíveis e substitui UUIDs por placeholders
 * 
 * RELEASE: 1.0.0 - 2026-02-03 - Infraestrutura base para export/import
 */

import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import type { FlowExportFormat } from '@/types/flows';
import {
  ExternalReference,
  ExternalReferenceType,
  NODE_REFERENCE_FIELDS,
  TRIGGER_REFERENCE_FIELDS,
  REFERENCE_TYPE_CONFIGS,
  createPlaceholder,
} from './reference-types';

/** Logger para módulo de exportação */
const log = createLogger('FlowExport');

/**
 * Versão atual do formato de exportação
 */
export const EXPORT_FORMAT_VERSION = '1.0';

/**
 * Interface para node do fluxo com tipagem flexível
 */
interface FlowNode {
  id: string;
  type: string;
  position: { x: number; y: number };
  data: {
    type: string;
    label?: string;
    config?: Record<string, unknown>;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

/**
 * Interface para edge do fluxo
 */
interface FlowEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
  [key: string]: unknown;
}

/**
 * Interface para trigger config
 */
interface TriggerConfig {
  type: string;
  [key: string]: unknown;
}

/**
 * Exporta um fluxo para o formato padronizado
 * 
 * @param flowId - ID do fluxo a exportar
 * @returns Objeto FlowExportFormat pronto para download
 */
export async function exportFlow(flowId: string): Promise<FlowExportFormat> {
  log.debug('Iniciando exportação', { flowId });

  // 1. Buscar fluxo do banco
  const { data: flow, error } = await supabase
    .from('flows')
    .select('*')
    .eq('id', flowId)
    .single();

  if (error || !flow) {
    log.error('Erro ao buscar fluxo', { error });
    throw new Error(`Fluxo não encontrado: ${flowId}`);
  }

  // 2. Extrair referências externas e substituir por placeholders
  const externalReferences: ExternalReference[] = [];
  
  // Processar nodes — sanitizar propriedades internas do ReactFlow v12+
  const nodes = (flow.nodes as unknown as FlowNode[]) || [];
  const sanitizedNodes = nodes.map(node => {
    const processed = processNode(node, externalReferences);
    // Remover propriedades internas do ReactFlow que não devem ser persistidas
    delete (processed as any).measured;
    delete (processed as any).selected;
    delete (processed as any).dragging;
    delete (processed as any).width;
    delete (processed as any).height;
    delete (processed as any).positionAbsolute;
    delete (processed as any).internals;
    delete (processed as any).resizing;
    return processed;
  });

  // Processar triggers — trigger_config pode ser objeto ou array
  const rawTriggers = flow.trigger_config;
  const triggers: TriggerConfig[] = Array.isArray(rawTriggers)
    ? (rawTriggers as unknown as TriggerConfig[])
    : rawTriggers && typeof rawTriggers === 'object'
      ? [rawTriggers as unknown as TriggerConfig]
      : [];
  const sanitizedTriggers = triggers.map(trigger =>
    processTrigger(trigger, externalReferences)
  );

  // 3. Coletar metadados das variáveis referenciadas
  const variableMetadata = await collectVariableMetadata(externalReferences, flowId);

  // 4. Montar estrutura de exportação
  const exportData: FlowExportFormat = {
    format_version: EXPORT_FORMAT_VERSION,
    export_date: new Date().toISOString(),
    source_app: 'UaizyChat',
    flow: {
      name: flow.name,
      description: flow.description || undefined,
      nodes: sanitizedNodes,
      edges: (flow.edges as unknown as FlowEdge[]) || [],
      variables: (flow.variables as unknown as Record<string, unknown>[]) || [],
      settings: (flow.settings as Record<string, unknown>) || {},
      trigger_config: sanitizedTriggers,
    },
    external_references: externalReferences,
    variable_metadata: variableMetadata,
  };

  log.info('Exportação concluída', {
    flowId,
    flowName: flow.name,
    nodeCount: sanitizedNodes.length,
    referenceCount: externalReferences.length,
    variableMetadataCount: variableMetadata.length,
  });

  return exportData;
}

/**
 * Processa um node, extraindo referências e substituindo por placeholders
 * 
 * RELEASE: 1.2.0 - 2026-02-03 - Adicionado sanitização de media_url para galeria
 */
function processNode(node: FlowNode, references: ExternalReference[]): FlowNode {
  const nodeType = node.data?.type || node.type;
  const fieldMappings = NODE_REFERENCE_FIELDS[nodeType];
  
  if (!fieldMappings || !node.data?.config) {
    return node;
  }

  // Clonar node para não modificar o original
  const processedNode: FlowNode = JSON.parse(JSON.stringify(node));
  const config = processedNode.data.config as Record<string, unknown>;

  for (const mapping of fieldMappings) {
    const idValue = config[mapping.path];
    const nameValue = mapping.nameField ? config[mapping.nameField] : undefined;

    // Só processa se tiver um UUID válido
    if (typeof idValue === 'string' && isValidUUID(idValue)) {
      const name = typeof nameValue === 'string' ? nameValue : `${mapping.type}_${idValue.slice(0, 8)}`;
      const placeholder = createPlaceholder(mapping.type, name);

      // Adicionar à lista de referências
      references.push({
        placeholder,
        type: mapping.type,
        originalId: idValue,
        name,
        context: `Node "${node.data.label || nodeType}" (${nodeType})`,
        nodeId: node.id,
        nodeType,
      });

      // Substituir UUID pelo placeholder
      config[mapping.path] = placeholder;
    }
  }

  // RELEASE: 1.2.0 - Limpar media_url quando for mídia da galeria interna
  // Isso evita vazamento de URLs do tenant de origem
  if (nodeType === 'message_media' && config.media_source === 'gallery') {
    config.media_url = '__INTERNAL_MEDIA__';
    log.debug('Media URL sanitizada para mídia da galeria', {
      nodeId: node.id,
      mediaName: config.media_gallery_name,
    });
  }

  // RELEASE: 2.0.0 - Tratar fixed_items[].product_id do charge_generate
  // Iterar array de itens fixos e substituir cada product_id por placeholder
  if (nodeType === 'charge_generate' && Array.isArray(config.fixed_items)) {
    const fixedItems = config.fixed_items as Array<Record<string, unknown>>;
    for (const item of fixedItems) {
      const productId = item.product_id;
      const productName = typeof item.product_name === 'string' ? item.product_name : `produto_${String(productId).slice(0, 8)}`;
      
      if (typeof productId === 'string' && isValidUUID(productId)) {
        const placeholder = createPlaceholder('crm_product', productName);
        
        references.push({
          placeholder,
          type: 'crm_product',
          originalId: productId,
          name: productName,
          context: `Node "${node.data.label || nodeType}" - Item fixo`,
          nodeId: node.id,
          nodeType,
        });
        
        item.product_id = placeholder;
      }
    }
  }

  return processedNode;
}

/**
 * Processa um trigger, extraindo referências de arrays de UUIDs
 * Usa campos de nome amigável (nameField) quando disponíveis para melhor auto-match
 * 
 * RELEASE: 1.0.0 - 2026-02-03 - Implementação inicial
 * RELEASE: 1.1.0 - 2026-02-03 - Suporte a nameField para nomes amigáveis
 */
function processTrigger(trigger: TriggerConfig, references: ExternalReference[]): TriggerConfig {
  // Clonar trigger para não modificar o original
  const processedTrigger: TriggerConfig = JSON.parse(JSON.stringify(trigger));

  for (const fieldMapping of TRIGGER_REFERENCE_FIELDS) {
    const value = processedTrigger[fieldMapping.path];
    // Tentar pegar o nome amigável do campo correspondente
    const namesValue = fieldMapping.nameField 
      ? processedTrigger[fieldMapping.nameField] 
      : undefined;

    if (fieldMapping.isArray && Array.isArray(value)) {
      // Processar array de UUIDs
      const namesArray = Array.isArray(namesValue) ? namesValue : [];
      
      const processedArray = value.map((item, index) => {
        if (typeof item === 'string' && isValidUUID(item)) {
          // Tentar pegar nome do array de nomes correspondente
          const name = (namesArray[index] && typeof namesArray[index] === 'string')
            ? namesArray[index]
            : `${fieldMapping.type}_${index + 1}`;
          const placeholder = createPlaceholder(fieldMapping.type, name);

          references.push({
            placeholder,
            type: fieldMapping.type,
            originalId: item,
            name,
            context: `Trigger "${trigger.type}" - ${REFERENCE_TYPE_CONFIGS[fieldMapping.type].label}`,
          });

          return placeholder;
        }
        return item;
      });

      processedTrigger[fieldMapping.path] = processedArray;
    } else if (typeof value === 'string' && isValidUUID(value)) {
      // Processar UUID único
      // Tentar pegar nome do campo correspondente
      const name = (typeof namesValue === 'string' && namesValue)
        ? namesValue
        : `${fieldMapping.type}_trigger`;
      const placeholder = createPlaceholder(fieldMapping.type, name);

      references.push({
        placeholder,
        type: fieldMapping.type,
        originalId: value,
        name,
        context: `Trigger "${trigger.type}"`,
      });

      processedTrigger[fieldMapping.path] = placeholder;
    }
  }

  return processedTrigger;
}

/**
 * Coleta metadados das variáveis referenciadas para permitir criação automática
 * na importação
 */
async function collectVariableMetadata(
  references: ExternalReference[],
  _flowId: string
): Promise<Array<{
  placeholder: string;
  name: string;
  display_name: string;
  type: string;
  is_system: boolean;
  description?: string;
}>> {
  // Filtrar apenas referências de variáveis
  const variableRefs = references.filter(r => r.type === 'tenant_variable');
  
  if (variableRefs.length === 0) {
    return [];
  }

  // Extrair IDs originais
  const variableIds = variableRefs.map(r => r.originalId);

  try {
    // Buscar detalhes das variáveis
    const { data, error } = await supabase
      .from('tenant_variables')
      .select('id, name, display_name, type, is_system, description')
      .in('id', variableIds);

    if (error) {
      log.warn('Erro ao buscar metadados de variáveis', { error });
      // Retorna metadados básicos baseados nas referências
      return variableRefs.map(ref => ({
        placeholder: ref.placeholder,
        name: ref.name.replace(/\s+/g, '_').toLowerCase(),
        display_name: ref.name,
        type: 'text',
        is_system: false,
      }));
    }

    // Mapear resultados
    const variableMap = new Map(
      (data || []).map(v => [v.id, v])
    );

    return variableRefs.map(ref => {
      const varData = variableMap.get(ref.originalId);
      
      if (varData) {
        return {
          placeholder: ref.placeholder,
          name: varData.name,
          display_name: varData.display_name || varData.name,
          type: varData.type || 'text',
          is_system: varData.is_system || false,
          description: varData.description || undefined,
        };
      }

      // Fallback para variáveis não encontradas
      return {
        placeholder: ref.placeholder,
        name: ref.name.replace(/\s+/g, '_').toLowerCase(),
        display_name: ref.name,
        type: 'text',
        is_system: false,
      };
    });
  } catch (err) {
    log.warn('Erro inesperado ao coletar metadados', { error: err });
    return variableRefs.map(ref => ({
      placeholder: ref.placeholder,
      name: ref.name.replace(/\s+/g, '_').toLowerCase(),
      display_name: ref.name,
      type: 'text',
      is_system: false,
    }));
  }
}

/**
 * Valida se uma string é um UUID válido
 */
function isValidUUID(value: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(value);
}

/**
 * Gera o nome do arquivo para download
 */
export function generateExportFileName(flowName: string): string {
  const sanitizedName = flowName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  
  const date = new Date().toISOString().slice(0, 10);
  return `fluxo-${sanitizedName}-${date}.uaizyflow`;
}

/**
 * Converte o formato de exportação para JSON string para download
 */
export function exportToJson(exportData: FlowExportFormat): string {
  return JSON.stringify(exportData, null, 2);
}

/**
 * Faz o download do arquivo de exportação
 */
export function downloadExportFile(exportData: FlowExportFormat, flowName: string): void {
  const jsonString = exportToJson(exportData);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = generateExportFileName(flowName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
}

/**
 * Conta quantas referências de cada tipo existem
 */
export function countReferencesByType(references: ExternalReference[]): Record<ExternalReferenceType, number> {
  const counts: Partial<Record<ExternalReferenceType, number>> = {};
  
  for (const ref of references) {
    counts[ref.type] = (counts[ref.type] || 0) + 1;
  }
  
  return counts as Record<ExternalReferenceType, number>;
}
